import { TitleComponent } from "Components/Title";
import styles from "./index.module.css";
import { SearchComponent } from "Components/SearchComponent";
import { ReactNode, useContext, useMemo, useState } from "react";
import verified from "assets/svg/Verified.svg";
import tonKeeperConnected from "assets/svg/ConnectedTon.svg";
import lock from "assets/svg/Lock.svg";
import briefcase from "assets/svg/Briefcase.svg";
import { useRequest } from "ahooks";
import { getCities, getDebateMetrics, InsuranceType } from "helpers/backend_helper";
import { NotificationContext } from "context/notificationContext";
import { AdminContext } from "context/adminContext";
import { Badge, Divider } from "antd";
import c2cToken from "assets/svg/c2c-token.svg";
import { InfoComponent } from "Components/InfoComponent";
import { TabsComponent } from "Components/TabsComponent";
import { RangePickerComponent } from "Components/RangePickerComponent";
import { RequestDisputes } from "./RequestDisputes";
import {
  PROP_NAME_CITY,
  PROP_NAME_FROM_DATE,
  PROP_NAME_INPUT_TEXT,
  PROP_NAME_INSURANCE,
  PROP_NAME_MAIN_TAB,
  PROP_NAME_SKIP_ADS,
  PROP_NAME_SORTBY,
  PROP_NAME_TO_DATE,
} from "helpers/constants";
import { useSearchParams } from "react-router-dom";
import { DebateStatuses } from "helpers/types";

export type StatusInfoUsers = "verivied" | "connected_ton_keeper" | "blocked" | "lessor";

export const getDesignedStatuses = (value: StatusInfoUsers) => {
  switch (value) {
    case "verivied":
      return <img src={verified} alt="verified" />;
    case "connected_ton_keeper":
      return <img src={tonKeeperConnected} alt="tonKeeperConnected" />;
    case "blocked":
      return <img src={lock} alt="lock" />;
    case "lessor":
      return <img src={briefcase} alt="briefcase" />;
    default:
      return <div>{value}</div>;
  }
};

const getItems = () => [
  {
    key: "0",
    label: (
      <Badge count={0} size="small" className={styles.badge}>
        <div className={styles.deals}>Requests</div>
      </Badge>
    ),
    children: <RequestDisputes />,
  },
  {
    key: "1",
    label: (
      <Badge count={0} size="small" className={styles.badge}>
        <div className={styles.deals}>In process</div>
      </Badge>
    ),
    children: <RequestDisputes />,
  },
  {
    key: "2",
    label: (
      <Badge count={0} size="small" className={styles.badge}>
        <div className={styles.deals}>Completed</div>
      </Badge>
    ),
    children: <RequestDisputes />,
  },
  {
    key: "3",
    label: (
      <Badge count={0} size="small" className={styles.badge}>
        <div className={styles.deals}>Declined</div>
      </Badge>
    ),
    children: <RequestDisputes />,
  },
];

export const STATUSES_ADMIN: DebateStatuses[] = ["WAIT", "IN_PROGRESS", "COMPLETED", "CANCELLED"];

export const NewDebates = () => {
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const { localCountry } = useContext(AdminContext);
  const [cities, setCities] = useState<{ label: string | ReactNode; value: string }[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const searchText = searchParams.get(PROP_NAME_INPUT_TEXT); // debounced
  const citiesFilter = searchParams.get(PROP_NAME_CITY);
  const choosenTab = searchParams.get(PROP_NAME_MAIN_TAB);
  const searchToDate = searchParams.get(PROP_NAME_TO_DATE);
  const searchFromDate = searchParams.get(PROP_NAME_FROM_DATE);
  const searchInsurance = searchParams.get(PROP_NAME_INSURANCE);

  const fromToDate = useMemo(
    () => ({
      from: searchFromDate || "",
      to: searchToDate || "",
    }),
    [searchFromDate, searchToDate],
  );

  const { loading: getCitiesLoading } = useRequest(() => getCities(localCountry.iso, true), {
    ready: !!localCountry?.iso,
    refreshDeps: [localCountry],
    onError: (error: any) => {
      openNotificationWithIcon("error", error.response?.data?.message || error.message);
    },
    onSuccess: (data) => {
      setCities(() => {
        return [
          { label: "All", value: "" },
          ...data.cities.map((city) => {
            const upperCaseFirstLt = city.formattedData[0].toUpperCase() + city.formattedData.slice(1);
            return { label: upperCaseFirstLt, value: city.id };
          }),
        ];
      });
    },
  });

  const { data: metrics, loading: isMetricsLoading } = useRequest(
    () =>
      getDebateMetrics(
        {
          debateStatus: STATUSES_ADMIN[choosenTab || "0"],
          searchText,
          cityId: citiesFilter?.length ? citiesFilter.split(",") : [],
          dateFrom: STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" ? searchFromDate : null,
          dateTo: STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" ? searchToDate : null,
          insuranceType: searchInsurance?.length ? (searchInsurance.split(",") as InsuranceType[]) : [],
        },
        localCountry.iso.toUpperCase(),
      ),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry, searchParams],
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
    },
  );

  const items = useMemo(() => {
    return getItems();
  }, []);
  return (
    <div className={styles.container}>
      <div style={{ marginBottom: 20 }}>
        <TitleComponent text="Dispute" />
      </div>
      <SearchComponent
        inputData={{
          palceHolder: "Search",
          queryTextName: PROP_NAME_INPUT_TEXT,
        }}
        selectData={[
          {
            options: cities,
            label: "City",
            isLoadingSelect: getCitiesLoading,
            queryName: PROP_NAME_CITY,
          },
          {
            isLoadingSelect: false,
            options: [
              { label: "All", value: "" },
              {
                label: "Deposit",
                value: "DEPOSIT",
              },
              {
                label: "Percentage",
                value: "PERCENTAGE",
              },
            ],
            label: "Insurance",
            queryName: PROP_NAME_INSURANCE,
          },
        ]}
      />
      <Divider style={{ borderWidth: 2 }} />
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        {[
          {
            infoOfData: "Amount of disputes",
            label: "Disputes",
            value: metrics?.disputes,
            moreInfo: metrics?.disputes_change
              ? {
                  isPositive: +metrics?.disputes_change > 0,
                  value:
                    +metrics?.disputes_change > 0 ? `+${metrics?.disputes_change}%` : `-${metrics?.disputes_change}%`,
                }
              : null,
            isLoading: isMetricsLoading,
          },
          {
            infoOfData: "Amount",
            label: "Amount",
            value: (
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <img src={c2cToken} alt="c2cToken" />
                <span>{metrics?.amount}</span>
              </div>
            ),
            moreInfo: metrics?.amount_change
              ? {
                  isPositive: +metrics?.amount_change?.toFixed(1) > 0,
                  value:
                    +metrics?.amount_change?.toFixed(1) > 0
                      ? `+${metrics?.amount_change?.toFixed(1)}%`
                      : `-${metrics?.amount_change?.toFixed(1)}%`,
                }
              : null,
            isLoading: isMetricsLoading,
          },
          STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" && {
            infoOfData: "Payout Rate",
            label: "Payout Rate",
            value: metrics?.payout_rate?.toFixed(4) + "%",
            moreInfo: metrics?.payout_rate_percent_change
              ? {
                  isPositive: +metrics?.payout_rate_percent_change?.toFixed(1) > 0,
                  value:
                    +metrics?.payout_rate_percent_change?.toFixed(1) > 0
                      ? `+${metrics?.payout_rate_percent_change?.toFixed(1)}%`
                      : `-${metrics?.payout_rate_percent_change?.toFixed(1)}%`,
                }
              : null,
            isLoading: isMetricsLoading,
          },
        ].map((v, i) => {
          if (!v) return null;
          const { infoOfData, label, value, isLoading, moreInfo } = v;
          return (
            <InfoComponent
              infoOfData={infoOfData}
              label={label}
              value={value}
              key={i}
              isLoading={isLoading}
              moreInfo={moreInfo}
            />
          );
        })}
      </div>
      <TabsComponent
        defaultActiveKey={choosenTab || "0"}
        items={items}
        className={styles.tabs}
        destroyInactiveTabPane
        onChange={(activeKey) => {
          searchParams.set(PROP_NAME_MAIN_TAB, items.find((v) => v.key === activeKey).key);
          searchParams.delete(PROP_NAME_SORTBY);
          searchParams.delete(PROP_NAME_SKIP_ADS);

          setSearchParams(searchParams, { replace: true });
        }}
        tabBarExtraContent={{
          right:
            STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" ? (
              <div
                style={{
                  position: "relative",
                  top: 5,
                  left: 20,
                }}
              >
                <RangePickerComponent
                  defaultValue={searchFromDate ? fromToDate : null}
                  onChange={(_, date) => {
                    const dateSplitedFrom = date[0].split(".");
                    const dateSplitedTo = date[1].split(".");
                    const dateCorrectFrom = `${dateSplitedFrom[2]}-${dateSplitedFrom[1]}-${dateSplitedFrom[0]}`;
                    const dateCorrectTo = `${dateSplitedTo[2]}-${dateSplitedTo[1]}-${dateSplitedTo[0]}`;

                    searchParams.set(PROP_NAME_FROM_DATE, dateCorrectFrom);
                    searchParams.set(PROP_NAME_TO_DATE, dateCorrectTo);
                    setSearchParams(searchParams);
                  }}
                  onDelete={() => {
                    searchParams.delete(PROP_NAME_FROM_DATE);
                    searchParams.delete(PROP_NAME_TO_DATE);
                    setSearchParams(searchParams);
                  }}
                />
              </div>
            ) : null,
        }}
      />
    </div>
  );
};
